// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { ChevronRight } from 'lucide-react';

import { ScopeTile } from '@/components/knowledge/ScopeTile';

export interface EntryRowProps {
  title: string;
  topic: string;
  version: number;
  relativeUpdated: string;
  editorLabel: string;
  isAgentEditor: boolean;
  pendingCount: number;
  tile: {
    scope: 'org' | 'integration' | 'app';
    integrationSlug?: string;
    integrationName?: string;
    appName?: string;
    appSlug?: string;
  };
  testId: string;
  /** Opens the entry in the shared entry editor drawer. */
  onOpen: () => void;
}

const sharedClassName =
  'w-full text-left px-4 md:px-5 py-3.5 hover:bg-muted/40 focus-visible:bg-muted/60 focus-visible:outline-none flex flex-col gap-2 md:gap-0';

export function EntryRow({
  title,
  topic,
  version,
  relativeUpdated,
  editorLabel,
  isAgentEditor,
  pendingCount,
  tile,
  testId,
  onOpen,
}: EntryRowProps): React.JSX.Element {
  const inner = (
    <div className="md:grid md:grid-cols-[minmax(0,1fr)_108px_190px_18px] md:items-center w-full gap-2 md:gap-0">
      {/* Entry cell */}
      <div className="flex items-center gap-3 min-w-0">
        <ScopeTile {...tile} size={36} />
        <div className="min-w-0">
          <div className="flex items-center min-w-0">
            <span className="text-[13.5px] font-semibold truncate">{title}</span>
            {pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center rounded-full bg-gl-amber/15 px-2 py-0.5 text-[10.5px] font-medium text-gl-amber-text whitespace-nowrap">
                {pendingCount} pending
              </span>
            )}
          </div>
          <p className="font-mono text-[11px] text-muted-foreground truncate">
            {topic} · v{version}
          </p>
        </div>
      </div>

      {/* Updated cell */}
      <div className="md:contents">
        <p className="md:hidden text-[10px] uppercase tracking-[0.08em] text-muted-foreground font-mono">
          UPDATED
        </p>
        <span className="text-[12px] text-muted-foreground tabular-nums">{relativeUpdated}</span>
      </div>

      {/* Last editor cell */}
      <div className="md:contents">
        <p className="md:hidden text-[10px] uppercase tracking-[0.08em] text-muted-foreground font-mono">
          EDITOR
        </p>
        <span className="text-[12px] text-muted-foreground truncate">
          {editorLabel}
          {isAgentEditor && (
            <span className="ml-1.5 font-mono text-[10px] rounded bg-muted px-1 py-0.5 text-muted-foreground">
              agent
            </span>
          )}
        </span>
      </div>

      {/* Trailing cell */}
      <div className="hidden md:flex justify-end text-muted-foreground">
        <ChevronRight className="h-4 w-4" aria-hidden />
      </div>
    </div>
  );

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={testId}
      aria-label={`Edit ${title}`}
      className={sharedClassName}
    >
      {inner}
    </button>
  );
}
